import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'environments/environment';
import ApiResponse from 'models/api-response/api-response.model';
import Log from 'models/log/log.model';
import ArcgisList from 'models/arcgis-list/arcgis-list.model';
import EsriLayerList from 'models/esri-layer-list/esri-layer-list.model';
import EsriService from 'models/esri-service/esri-service.model';
import ArcgisToken from 'models/arcgis-token/arcgis-token.model';
import { LogService } from 'services/log/log.service';
import { ApiRequestService } from 'services/api-request/api-request.service';
import * as qs from 'qs';

@Injectable({
  providedIn: 'root',
})
export class ArcgisService {
  private folderList: BehaviorSubject<ArcgisList> = new BehaviorSubject(new ArcgisList());
  private serviceList: BehaviorSubject<ArcgisList> = new BehaviorSubject(new ArcgisList());
  private layerList: BehaviorSubject<EsriLayerList> = new BehaviorSubject(new EsriLayerList());
  constructor(
    public apiRequestService: ApiRequestService,
    public http: HttpClient,
    public logService: LogService,
  ) { }

  public async listFolders() {
    return new Promise((resolve: (r: ArcgisList) => any, reject: (e: Error) => any) => {
      this.apiRequestService.get('/arcgis/folderlist').subscribe({
        next: async (res: ApiResponse<ArcgisList>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'ArcgisService.folderlist',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          return resolve(new ArcgisList(res.data));
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ArcgisService.folderlist',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async getAccessToken() {
    return new Promise((resolve: (r: ArcgisToken) => any, reject: (e: Error) => any) => {
      this.http.post<any>(environment.arcgisTokenUrl, qs.stringify({
        username: environment.arcgisUser,
        password: environment.arcgisPass,
        client: 'requestip',
        expiration: '43200',
        f: 'json'
      }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
        }).subscribe({
          next: async res => {
            return resolve(new ArcgisToken(res));
          },
          error: async error => {
            console.log(error);
            return reject(new Error('requestError'));
          }
        });
    });
  }

  public async refreshFolderList(): Promise<ArcgisList> {
    return new Promise(async (resolve: (r: ArcgisList) => any, reject: (e: Error) => any) => {
      const res: ArcgisList = await this.listFolders();
      this.folderList.next(res);
      return resolve(res);
    });
  }

  public async listServices(folder: string) {
    return new Promise((resolve: (r: ArcgisList) => any, reject: (e: Error) => any) => {
      this.apiRequestService.post('/arcgis/servicelist', {
        folder
      }).subscribe({
        next: async (res: ApiResponse<ArcgisList>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'ArcgisService.servicelist',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          return resolve(new ArcgisList(res.data));
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ArcgisService.servicelist',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async refreshServiceList(folder: string): Promise<ArcgisList> {
    return new Promise(async (resolve: (r: ArcgisList) => any, reject: (e: Error) => any) => {
      const res: ArcgisList = await this.listServices(folder);
      this.serviceList.next(res);
      return resolve(res);
    });
  }

  public async listLayers(service: EsriService) {
    return new Promise((resolve: (r: EsriLayerList) => any, reject: (e: Error) => any) => {
      this.apiRequestService.post('/arcgis/layerlist', {
        service
      }).subscribe({
        next: async (res: ApiResponse<EsriLayerList>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'ArcgisService.layerlist',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          return resolve(new EsriLayerList(res.data));
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'ArcgisService.layerlist',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async refreshLayerList(service: EsriService): Promise<EsriLayerList> {
    return new Promise(async (resolve: (r: EsriLayerList) => any, reject: (e: Error) => any) => {
      const res: EsriLayerList = await this.listLayers(service);
      this.layerList.next(res);
      return resolve(res);
    });
  }

  getFolderList(): BehaviorSubject<ArcgisList> {
    return this.folderList;
  }

  getServiceList(): BehaviorSubject<ArcgisList> {
    return this.serviceList;
  }

  getLayerList(): BehaviorSubject<EsriLayerList> {
    return this.layerList;
  }
}
